import { apiClient } from '../apiClient';

// The readiness formula and every scored breakdown are served by the API
// (GET /lms/readiness/formula, and `breakdown` on the home + org payloads).
// Nothing in the UI may hardcode a weight: v1 weights were duplicated in TSX
// copy and silently drifted from the Go scorer, which is how the org portal
// ended up describing inputs the score never had.

export type ReadinessPillarKey = 'ability' | 'engagement';

/** One scored input inside a pillar, as defined by the formula. */
export interface ReadinessFormulaComponent {
  key: string;
  label: string;
  description: string;
  /** Relative to its pillar, 0..1. Components of a pillar sum to 1. */
  weight: number;
}

export interface ReadinessFormulaPillar {
  key: ReadinessPillarKey;
  label: string;
  description: string;
  /** Share of the whole score, 0..1. Pillars sum to 1. */
  weight: number;
  components: ReadinessFormulaComponent[];
}

/** One readiness band with its inclusive lower bound. */
export interface ReadinessTier {
  key: string;
  label: string;
  min_score: number;
}

export interface ReadinessFormula {
  version: number;
  summary: string;
  pillars: ReadinessFormulaPillar[];
  tiers: ReadinessTier[];
  /** Per-skill score at or above which a skill counts as demonstrated. */
  competence_threshold: number;
  renormalisation_note: string;
}

/** One component's contribution to a scored breakdown. */
export interface ReadinessComponentResult {
  key: string;
  label: string;
  /** `null` means "no data yet" — it is NOT a measured zero. */
  value: number | null;
  /** Nominal weight within the pillar, 0..1. */
  weight: number;
  /** Weight actually applied after excluded siblings were rescaled, 0..1. */
  effective_weight: number;
  excluded: boolean;
  excluded_reason?: string;
}

export interface ReadinessPillarResult {
  key: ReadinessPillarKey;
  label: string;
  /** `null` when the whole pillar had no data and was excluded. */
  score: number | null;
  weight: number;
  effective_weight: number;
  excluded: boolean;
  components: ReadinessComponentResult[];
}

export interface ReadinessBreakdown {
  version: number;
  score: number;
  /** '' when `has_data` is false — there is no band for an unmeasured score. */
  tier: string;
  /** Share of nominal weight that had data behind it, 0..1. */
  coverage: number;
  /** false => render an empty state, never a score of 0. */
  has_data: boolean;
  pillars: ReadinessPillarResult[];
}

// --- evidence (the records behind a component's score) ---
//
// Served to org/TPO callers only: a score a placement officer cannot
// interrogate is a score they will not defend to a student. The breakdown says
// attendance is 33; the evidence says which sessions were missed.

/** How an item contributed. Drives affordance only — the `value` text always stays. */
export type ReadinessEvidenceStatus = 'met' | 'missed' | 'partial' | 'neutral';

/**
 * What a client may turn into a route. The API deliberately sends no URLs;
 * routing belongs to the frontend, so each surface maps these itself.
 */
export type ReadinessEvidenceLinkKind = 'assessment' | 'assignment' | 'session' | 'problem';

/** One underlying record: one challenge, one quiz, one session, one topic. */
export interface ReadinessEvidenceItem {
  label: string;
  detail?: string;
  /** Already formatted by the API ("Solved", "92%", "Absent") — render verbatim. */
  value: string;
  status: ReadinessEvidenceStatus;
  /** ISO-8601. */
  at?: string;
  link_kind?: ReadinessEvidenceLinkKind;
  link_id?: string;
}

/**
 * The status distribution behind a component, over the WHOLE set.
 *
 * This is the part of the payload that does not grow with the data: a batch
 * with 327 curriculum topics sends the same five integers as one with three.
 * Every "shape" affordance in the UI is drawn from here and never from
 * `items.length`, which is only ever a preview.
 */
export interface ReadinessEvidenceCounts {
  total: number;
  met: number;
  partial: number;
  missed: number;
  neutral: number;
}

export interface ReadinessComponentEvidence {
  /** The arithmetic in words, always over the FULL count — never the preview. */
  summary: string;
  /** Describes the whole set, whatever `items` happens to contain. */
  counts: ReadinessEvidenceCounts;
  /** The few worst records, worst-first. A preview, not the list. Preserve the order. */
  items: ReadinessEvidenceItem[];
  /** True when the full set is larger than the preview — drives "View all N". */
  has_more: boolean;
}

/** Keyed by the same component keys the formula endpoint publishes. */
export interface ReadinessEvidence {
  components: Record<string, ReadinessComponentEvidence>;
}

/** One page of a single component's full evidence — the drill-down drawer. */
export interface ReadinessComponentEvidencePage {
  component: string;
  summary: string;
  /** Always the UNFILTERED distribution, so filter options can be counted. */
  counts: ReadinessEvidenceCounts;
  items: ReadinessEvidenceItem[];
  /** Matches AFTER filtering — what the pager is walking through. */
  total: number;
  limit: number;
  offset: number;
}

/** How many worst-first records the card payload previews per component. */
export const READINESS_EVIDENCE_PREVIEW_LIMIT = 5;

/** Page size for the drill-down drawer. The API caps `limit` at 200. */
export const READINESS_EVIDENCE_PAGE_SIZE = 50;

/** Zero distribution, for components whose evidence hasn't arrived. */
export function emptyEvidenceCounts(): ReadinessEvidenceCounts {
  return { total: 0, met: 0, partial: 0, missed: 0, neutral: 0 };
}

export const readinessApi = {
  getFormula: () =>
    apiClient.get<ReadinessFormula>('/api/v1/lms/readiness/formula'),
};
